import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../foundation/cn';
import { TONE_CLASSES, type Tone } from '../foundation/tone';
import { XIcon } from '../foundation/icons';

export interface ToastOptions {
  readonly title: string;
  readonly description?: string;
  readonly tone?: Tone;
  readonly durationMs?: number;
}

interface ToastItem extends ToastOptions {
  readonly id: number;
}

interface ToastApi {
  readonly show: (opts: ToastOptions) => void;
  readonly dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

// Toast infrastructure: a provider owning the queue + a portal-rendered region. Presentation
// only; callers push messages via useToast().
export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [items, setItems] = useState<readonly ToastItem[]>([]);
  const seq = useRef(0);

  const dismiss = useCallback((id: number) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    (opts: ToastOptions) => {
      const id = ++seq.current;
      setItems((prev) => [...prev, { ...opts, id }]);
      const duration = opts.durationMs ?? 5000;
      if (duration > 0) {
        window.setTimeout(() => dismiss(id), duration);
      }
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(() => ({ show, dismiss }), [show, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div
          className="pointer-events-none fixed inset-x-0 bottom-0 z-toast flex flex-col items-center gap-2 p-4"
          role="region"
          aria-label="შეტყობინებები"
        >
          {items.map((t) => (
            <div
              key={t.id}
              role="status"
              className={cn(
                'pointer-events-auto w-full max-w-sm animate-fade-in rounded-token border border-border p-3 shadow-e2',
                'bg-surface',
              )}
            >
              <div className="flex items-start gap-2">
                <span className={cn('mt-1 h-2 w-2 shrink-0 rounded-full', TONE_CLASSES[t.tone ?? 'neutral'])} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-fg">{t.title}</p>
                  {t.description !== undefined && (
                    <p className="mt-0.5 text-xs text-fg-muted">{t.description}</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => dismiss(t.id)}
                  aria-label="დახურვა"
                  className="text-fg-muted hover:text-fg"
                >
                  <XIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (ctx === null) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return ctx;
}
